/*
  Mirrors the currently-selected kit tier's price into a compact element placed
  where the section's own price block would normally sit, near the top of the
  buy box. Exists because that price block is deliberately suppressed on
  kit-picker products (see the comment in main-product.liquid), since a plain
  static price cannot track which bundle tier is selected and would go stale
  the moment the customer changes it.

  This does not compute any price itself. It only copies text that
  build-the-kit.liquid and build-the-kit.js already render correctly (each
  tier's own [data-kit-price] span, which build-the-kit.js keeps in sync on
  both tier changes and variant-driven price refetches). A MutationObserver on
  the currently-selected tier's price span means this stays correct through
  every path that already updates that span, without needing to know about or
  duplicate any of them.
*/
(() => {
  function initOne(productInfo) {
    const summary = productInfo.querySelector('[data-kit-summary-price]');
    const kitPicker = productInfo.querySelector('kit-picker');
    if (!summary || !kitPicker) return;

    let observer = null;
    let shownText = null;

    function tierPriceEls(tierEl) {
      if (!tierEl) return null;
      return {
        price: tierEl.querySelector('[data-kit-price]'),
        was: tierEl.querySelector('[data-kit-price-was]'),
      };
    }

    function render(tierEl) {
      const els = tierPriceEls(tierEl);
      if (!els || !els.price) return;
      const priceText = els.price.textContent.trim();
      const wasText = els.was ? els.was.textContent.trim() : '';
      // Named, so a bundle total never reads as the harness price the collection card showed.
      const tierName = tierEl.querySelector('.kit-tier__label')?.textContent.trim() || '';
      // Keep the <s> strikethrough, not flattened plain text: without it
      // "$21.96 $24.96" reads as two unrelated numbers instead of a sale
      // price against the price it was.
      summary.textContent = '';
      if (tierName) {
        const label = document.createElement('span');
        label.className = 'kit-summary-price__tier';
        label.textContent = `${tierName}:`;
        summary.append(label, ' ');
      }
      summary.append(document.createTextNode(priceText));
      if (wasText) {
        summary.append(' ');
        const s = document.createElement('s');
        s.textContent = wasText;
        summary.append(s);
      }
      // The first sync only mirrors the server render, so only a later change ticks.
      if (shownText !== null && summary.textContent !== shownText && typeof tickPrice === 'function') tickPrice(summary);
      shownText = summary.textContent;
    }

    function currentTierEl() {
      // selectedTier is the source of truth once the component has hydrated.
      // Before that, fall back to whichever tier's radio is checked in the
      // markup (the server-rendered default), so the very first paint is
      // already correct rather than empty.
      if (kitPicker.selectedTier) return kitPicker.selectedTier;
      const checked = kitPicker.querySelector('input[type="radio"]:checked');
      return checked ? checked.closest('[data-tier]') : null;
    }

    function observe(tierEl) {
      if (observer) observer.disconnect();
      const els = tierPriceEls(tierEl);
      if (!els || !els.price) return;
      observer = new MutationObserver(() => render(tierEl));
      observer.observe(els.price, { characterData: true, childList: true, subtree: true });
      if (els.was) observer.observe(els.was, { characterData: true, childList: true, subtree: true });
    }

    function sync() {
      const tierEl = currentTierEl();
      render(tierEl);
      observe(tierEl);
    }

    sync();

    // Tier switches happen via a native radio change on each tier's input.
    // Listening here rather than patching selectTier keeps this file fully
    // independent of build-the-kit.js's own internals.
    kitPicker.addEventListener('change', (evt) => {
      if (evt.target.matches('input[type="radio"]')) sync();
    });
  }

  function init() {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      if (productInfo.querySelector('[data-kit-summary-price]')) initOne(productInfo);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
